// Copyright 2025. WebPros International GmbH. All rights reserved.

import {
    Heading,
    Icon,
    List,
    Text,
    Translate,
} from '@plesk/ui-library';
import {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { IComputeResourceRecoveryReadinessStatusResponse } from 'common/api/resources/DisasterRecovery';
import { ListColumn } from '@plesk/ui-library/types/src/components/List';
import DestinationComputeResourceParamStatus from 'admin/disasterRecovery/components/DestinationComputeResourceParamStatus/DestinationComputeResourceParamStatus';
import IpBlocksConflictDialog from 'admin/disasterRecovery/components/IpBlocksConflictDialog/IpBlocksConflictDialog';
import StatusConflictDialog from 'admin/disasterRecovery/components/StatusConflictDialog/StatusConflictDialog';
import StorageTypesConflictDialog from 'admin/disasterRecovery/components/StorageTypesConflictDialog/StorageTypesConflictDialog';
import VirtualizationTypesConflictDialog from 'admin/disasterRecovery/components/VirtualizationTypesConflictDialog/VirtualizationTypesConflictDialog';
import SourceTypesConflictDialog from 'admin/disasterRecovery/components/SourceTypesConflictDialog/SourceTypesConflictDialog';
import ArchTypesConflictDialog from 'admin/disasterRecovery/components/ArchTypesConflictDialog/ArchTypesConflictDialog';
import CapabilitiesConflictDialog from 'admin/disasterRecovery/components/CapabilitiesConflictDialog/CapabilitiesConflictDialog';
import OverallDialog from 'admin/disasterRecovery/components/OverallDialog/OverallDialog';

type ComputeResourceWithCompatibilityDetails = IComputeResourceRecoveryReadinessStatusResponse['destination_compute_resources'][number];

export const ICONS_DATA_CY = {
    OVERALL: 'overall',
    STATUS: 'status',
    IP_BLOCKS: 'ip_blocks',
    VIRTUALIZATION_TYPE: 'virtualization_type',
    STORAGES: 'storages',
    SOURCE_TYPE: 'source_type',
    ARCH_TYPE: 'arch_type',
    CAPABILITIES: 'capabilities',
};

type WindowType =
    'overall' |
    'status' |
    'ip-blocks' |
    'virtualization-types' |
    'storage-types' |
    'source-type' |
    'arch-type' |
    'capabilities';

export default function DestinationComputeResourcesTable({
    computeResourcesWithCompatibilityDetails,
}: {
    computeResourcesWithCompatibilityDetails: ComputeResourceWithCompatibilityDetails[];
}) {

    const [activeCrWithCd, setActiveCrWithCd] = useState<ComputeResourceWithCompatibilityDetails | null>(null);

    const [activeWindowType, setActiveWindowType] = useState<WindowType | null>(null);

    const handleOpenDetailsWindow = useCallback(
        (crWithCd: ComputeResourceWithCompatibilityDetails, windowType: WindowType) => {
            setActiveCrWithCd(crWithCd);
            setActiveWindowType(windowType);
        },
        []
    );

    const handleCloseDetailsWindow = () => {
        setActiveWindowType(null);
    };

    const columns = useMemo(
        (): Array<ListColumn<ComputeResourceWithCompatibilityDetails>> => [
            {
                key: 'computeResource',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.computeResource" />,
                render: (row) => row.compute_resource.name,
            },
            {
                key: 'overall',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.overall" />,
                align: 'center',
                render: (row) => (
                    <DestinationComputeResourceParamStatus
                        data-cy={ICONS_DATA_CY.OVERALL}
                        value={row.compatibility_details.is_compatible}
                        onClick={() => handleOpenDetailsWindow(row, 'overall')}
                    />
                ),
            },
            {
                key: 'status',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.status" />,
                align: 'center',
                render: (row) => (
                    <DestinationComputeResourceParamStatus
                        data-cy={ICONS_DATA_CY.STATUS}
                        value={row.compatibility_details.destination_cr_is_active}
                        onClick={() => handleOpenDetailsWindow(row, 'status')}
                    />
                ),
            },
            {
                key: 'ip-blocks',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.ipBlocks" />,
                align: 'center',
                render: (row) => {
                    const value = row.compatibility_details.compatible_destination_cr_ip_blocks_count ===
                        row.compatibility_details.source_cr_ip_blocks_count;

                    const additionalText = `${row.compatibility_details.compatible_destination_cr_ip_blocks_count}/${row.compatibility_details.source_cr_ip_blocks_count}`;

                    return (
                        <DestinationComputeResourceParamStatus
                            data-cy={ICONS_DATA_CY.IP_BLOCKS}
                            value={value}
                            additionalText={additionalText}
                            onClick={() => handleOpenDetailsWindow(row, 'ip-blocks')}
                        />
                    );
                },
            },
            {
                key: 'virtualization-types',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.virtualizationTypes" />,
                align: 'center',
                render: (row) => {
                    const kvmNotSupported =
                        'kvm' in row.compatibility_details.supported_virtualization_types &&
                        !row.compatibility_details.supported_virtualization_types.kvm;

                    const vzNotSupported =
                        'vz' in row.compatibility_details.supported_virtualization_types &&
                        !row.compatibility_details.supported_virtualization_types.vz;

                    return (
                        <DestinationComputeResourceParamStatus
                            data-cy={ICONS_DATA_CY.VIRTUALIZATION_TYPE}
                            value={!(kvmNotSupported || vzNotSupported)}
                            onClick={() => handleOpenDetailsWindow(row, 'virtualization-types')}
                        />
                    );
                },
            },
            {
                key: 'storage-types',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.storageTypes"/>,
                align: 'center',
                render: (row) => (
                    <DestinationComputeResourceParamStatus
                        data-cy={ICONS_DATA_CY.STORAGES}
                        value={row.compatibility_details.storages_is_compatible}
                        onClick={() => handleOpenDetailsWindow(row, 'storage-types')}
                    />
                ),
            },
            {
                key: 'source-type',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.sourceType"/>,
                align: 'center',
                render: (row) => (
                    <DestinationComputeResourceParamStatus
                        data-cy={ICONS_DATA_CY.SOURCE_TYPE}
                        value={row.compatibility_details.sources_is_compatible && row.compatibility_details.products_is_compatible}
                        onClick={() => handleOpenDetailsWindow(row, 'source-type')}
                    />
                ),
            },
            {
                key: 'arch-type',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.archType"/>,
                align: 'center',
                render: (row) => (
                    <DestinationComputeResourceParamStatus
                        data-cy={ICONS_DATA_CY.ARCH_TYPE}
                        value={row.compatibility_details.arch_type_is_compatible}
                        onClick={() => handleOpenDetailsWindow(row, 'arch-type')}
                    />
                ),
            },
            {
                key: 'capabilities',
                title: <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.capabilities"/>,
                align: 'center',
                render: (row) => (
                    <DestinationComputeResourceParamStatus
                        data-cy={ICONS_DATA_CY.CAPABILITIES}
                        value={
                            row.compatibility_details.destination_cr_capabilities_exists &&
                            row.compatibility_details.destination_cr_os_is_supported
                        }
                        onClick={() => handleOpenDetailsWindow(row, 'capabilities')}
                    />
                ),
            },
        ],
        [handleOpenDetailsWindow]
    );

    return (
        <>
            <div style={{ marginBottom: 16 }}>
                <Heading level={3}>
                    <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.title" />
                </Heading>

                {!!computeResourcesWithCompatibilityDetails.length && (
                    <div>
                        <Icon
                            name={'exclamation-mark-circle'}
                            intent={'danger'}
                            style={{ marginRight: 8 }}
                        />

                        <Text fontSize="sm">
                            <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.exclamationIconHelperText" />
                        </Text>
                    </div>
                )}

                <List
                    columns={columns}
                    data={computeResourcesWithCompatibilityDetails}
                    rowKey={(item) => String(item.compute_resource.id)}
                    emptyView={(
                        <Text component={'p'}>
                            <Translate content={'disasterRecovery.readinessStatus.destinationComputeResources.emptyList'} />
                        </Text>
                    )}
                />
            </div>

            <OverallDialog
                isOpen={activeWindowType === 'overall'}
                onClose={handleCloseDetailsWindow}
                compatibilityDetails={activeCrWithCd?.compatibility_details ?? null}
            />

            <StatusConflictDialog
                isOpen={activeWindowType === 'status'}
                onClose={handleCloseDetailsWindow}
                status={activeCrWithCd?.compute_resource.status}
            />

            <IpBlocksConflictDialog
                isOpen={activeWindowType === 'ip-blocks'}
                onClose={handleCloseDetailsWindow}
                ipBlocks={activeCrWithCd?.compatibility_details.missed_ip_blocks ?? []}
            />

            <VirtualizationTypesConflictDialog
                isOpen={activeWindowType === 'virtualization-types'}
                onClose={handleCloseDetailsWindow}
                compatibilityDetails={activeCrWithCd?.compatibility_details ?? null}
            />

            <StorageTypesConflictDialog
                isOpen={activeWindowType === 'storage-types'}
                onClose={handleCloseDetailsWindow}
                storages={activeCrWithCd?.compatibility_details.incompatible_storages ?? []}
            />

            <SourceTypesConflictDialog
                isOpen={activeWindowType === 'source-type'}
                onClose={handleCloseDetailsWindow}
                compatibilityDetails={activeCrWithCd?.compatibility_details ?? null}
            />

            <ArchTypesConflictDialog
                isOpen={activeWindowType === 'arch-type'}
                onClose={handleCloseDetailsWindow}
            />

            <CapabilitiesConflictDialog
                isOpen={activeWindowType === 'capabilities'}
                onClose={handleCloseDetailsWindow}
                compatibilityDetails={activeCrWithCd?.compatibility_details ?? null}
            />
        </>
    );
}
